<template>
  <div class="brochure-spread-display">
    <div class="brochure-spread-container">
      <!-- Prev spread button -->
      <BrochureButton
        v-if="prevSpread"
        direction="back"
        class="nav-button back allow-touch"
        @click="prevPage"
        @dblclick.prevent.stop
      />
      <PanZoom
        v-model:zoom-level="zoomLevel"
        :is-active="isPanZoomActive && !isEmbed"
      >
        <!-- The previous spread, needed for the transition -->
        <BrochureSpread
          v-if="prevSpread && zoomLevel === 1"
          class="prev-spread"
          :spread="prevSpread"
          :hotspot-mode="hotspotMode"
        />

        <!-- The current spread -->
        <BrochureSpread
          v-if="currentSpread"
          :spread="currentSpread"
          class="current-spread"
          :hotspot-mode="hotspotMode"
        />

        <!-- The next spread, needed for the transition -->
        <BrochureSpread
          v-if="nextSpread && zoomLevel === 1"
          class="next-spread"
          :spread="nextSpread"
          :hotspot-mode="hotspotMode"
        />
      </PanZoom>

      <!-- Next spread button -->
      <BrochureButton
        v-if="nextSpread"
        direction="forward"
        class="nav-button forward allow-touch"
        @click="nextPage"
        @dblclick.prevent.stop
      />
    </div>
    <BrochureZoomShare
      v-if="!isEmbed"
      v-model:zoom-level="zoomLevel"
      :brochure-title="brochureTitle"
      :show-zoom="true"
      :has-prev="!!prevSpread"
      :has-next="!!nextSpread"
      @first="pagenumber = 0"
      @last="pagenumber = spreads[spreads.length - 1][0]?.index ?? 0"
      @next="nextPage"
      @prev="prevPage"
    />
  </div>
</template>

<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import useTimer from "~/composables/useTimer";
import {
  HotspotMode,
  type PageFragment,
  useCreatePageTimeStatisticMutation,
  useCreatePageVisitStatisticMutation,
} from "~/graphql/generated";

type Spread = [PageFragment | null, PageFragment | null];

const props = defineProps<{
  spreads: Spread[];
  hotspotMode: HotspotMode;
  brochureTitle: string;
}>();

const pagenumber = useRouteNumberParam("pagenumber", 0);
const zoomLevel = ref(1);
const isEmbed = isEmbedded();

// Get the prev, current and next spread
const currentSpread = computed(() => getSpread(pagenumber.value));
const nextSpread = computed(() => getSpread(pagenumber.value + 2));
const prevSpread = computed(() => getSpread(pagenumber.value - 2));

// Get the spread by index
const getSpread = (index: number) => {
  // Catch issue with the 2nd page being on the 2nd spread instead of the first
  if (index === -1) index = 0;
  // Catch issue with 2-page brochures
  if (index === 2) index = 1;

  return (
    props.spreads.find((spread) => {
      if (spread[0]?.index === index || spread[1]?.index === index) return true;
      return false;
    }) ?? null
  );
};

const goToPage = function (spread: Spread) {
  zoomLevel.value = 1;

  // Check if the first entry of the spread has a page index (if not is it the ad page and we use the right page's index)

  if (spread[0]) {
    pagenumber.value = spread[0].index;
  } else if (spread[1]) {
    pagenumber.value = spread[1].index;
  }
};

// Pause the panzoom when there is an active hotspot
const activeHotspot = useRouteQuery("offer");
const isPanZoomActive = computed(() => !activeHotspot.value);

const nextPage = function () {
  if (nextSpread.value) goToPage(nextSpread.value);
};
const prevPage = function () {
  if (prevSpread.value) goToPage(prevSpread.value);
};

// Expose prev and next page methods to the parent component
defineExpose({ nextPage, prevPage });

// Page visit statistic mutation
const deviceId = useDeviceId();
const createPageVisit = useCreatePageVisitStatisticMutation();
const createPageTimeStatistic = useCreatePageTimeStatisticMutation();

// Time components
const { startTimer, pauseTimer, resumeTimer, resetTimer, getTimeInSeconds } =
  useTimer();
startTimer();
const windowFocus = useWindowFocus();
watch(windowFocus, (isFocused) => {
  if (!isFocused) pauseTimer();
  if (isFocused) resumeTimer();
});

// Execute the page visit and page time mutation on each page number change
watch(
  pagenumber,
  (_, prevPage) => {
    // The left page
    if (currentSpread.value?.[0]) {
      createPageVisit.executeMutation({
        createPageVisitStatistic: {
          deviceId: deviceId.value,
          pageId: currentSpread.value?.[0].id,
        },
      });
    }
    // The right page
    if (currentSpread.value?.[1]) {
      createPageVisit.executeMutation({
        createPageVisitStatistic: {
          deviceId: deviceId.value,
          pageId: currentSpread.value?.[1].id,
        },
      });
    }

    // The left page of the previous spread (on page change we send the time for the previous page to the server)
    if (prevPage) {
      const prevVisitedSpread = getSpread(prevPage);

      // Left page
      if (prevVisitedSpread?.[0]) {
        createPageTimeStatistic.executeMutation({
          createPageTimeStatistic: {
            deviceId: deviceId.value,
            pageId: prevVisitedSpread[0].id,
            timeInSeconds: getTimeInSeconds() / 2, // Divide by two pages
          },
        });
      }
      // Right page
      if (prevVisitedSpread?.[1]) {
        createPageTimeStatistic.executeMutation({
          createPageTimeStatistic: {
            deviceId: deviceId.value,
            pageId: prevVisitedSpread[1].id,
            timeInSeconds: getTimeInSeconds() / 2, // Divide by two pages
          },
        });
      }

      // Reset the timer
      resetTimer();
    }
  },
  { immediate: true }
);

// Also (try to) send a statistics mutation event on the beforeunload (window close) event
onMounted(() => {
  useEventListener(document, "beforeunload", () => {
    // Left page
    if (currentSpread.value?.[0]) {
      createPageTimeStatistic.executeMutation({
        createPageTimeStatistic: {
          deviceId: deviceId.value,
          pageId: currentSpread.value?.[0].id,
          timeInSeconds: getTimeInSeconds() / 2, // Divide by two pages
        },
      });
    }
    // Right page
    if (currentSpread.value?.[1]) {
      createPageTimeStatistic.executeMutation({
        createPageTimeStatistic: {
          deviceId: deviceId.value,
          pageId: currentSpread.value?.[1].id,
          timeInSeconds: getTimeInSeconds() / 2, // Divide by two pages
        },
      });
    }
  });
});
</script>

<style lang="scss" scoped>
.brochure-spread-display {
  height: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
}
.brochure-spread-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.nav-button {
  position: absolute;
  top: 50%;
  z-index: 4;

  &.back {
    left: 1.5rem;
  }

  &.forward {
    right: 1.5rem;
  }
}
</style>
